import api from './api';

/** Request: documentos a liquidar en un período. */
export interface DocumentosLiquidablesRequest {
  nroVendedor: number;
  desde: string;
  hasta: string;
  /** null / omitir = todas las sucursales; > 0 filtra. */
  nroSuc?: number | null;
  soloTiposQueLiquidan?: boolean;
}

export interface DocumentoLiquidableDto {
  nroFormulario: number;
  fechaEmision?: string | null;
  nroTipoFormulario?: string | null;
  tipoFormularioDesc?: string | null;
  letra?: string | null;
  nroSuc?: number | null;
  nroComprobante?: number | null;
  formularioDisplay?: string | null;
  nroCliente?: number | null;
  nombreCliente?: string | null;
  comisionBase?: string | null;
  comisionPorc?: number | null;
  impNeto?: number | null;
  impNetoMasImp?: number | null;
  impComision?: number | null;
}

export interface DocumentosLiquidablesResult {
  nroVendedor: number;
  nombreVendedor?: string | null;
  nroProveedor?: number | null;
  nombreProveedor?: string | null;
  nroCuentaLiquidacion?: number | null;
  nombreCuentaLiquidacion?: string | null;
  comisionPorc?: number | null;
  comisionBase?: string | null;
  documentos: DocumentoLiquidableDto[];
  subtotalComision: number;
  cantidadFilas: number;
}

export interface LiquidarComisionRequest {
  nroVendedor: number;
  fechaEmision: string;
  /** Sucursal del comprobante X a generar (obligatorio > 0). */
  nroSuc: number;
  nroFormularios: number[];
  concCom1?: string;
  concComImp1?: number;
  concCom2?: string;
  concComImp2?: number;
}

export interface LiquidarComisionResult {
  exitoso: boolean;
  mensaje?: string | null;
  nroFormularioComision?: number | null;
  letra?: string | null;
  nroSuc?: number | null;
  nroComprobante?: number | null;
  formularioDisplay?: string | null;
  nroProveedor?: number | null;
  nombreProveedor?: string | null;
  nroCuentaComision?: number | null;
  totalComision?: number | null;
  documentosLiquidados?: number | null;
  nroFormulariosOrigen?: number[] | null;
}

export interface ComisionLiquidacionListParams {
  desde?: string;
  hasta?: string;
  nroSuc?: number;
  nroVendedor?: number;
  pageNumber?: number;
  pageSize?: number;
}

export interface ComisionLiquidacionListItem {
  nroFormulario: number;
  fechaEmision?: string | null;
  letra?: string | null;
  nroSuc?: number | null;
  nroComprobante?: number | null;
  formularioDisplay?: string | null;
  nroVendedor?: number | null;
  nombreVendedor?: string | null;
  nroProveedor?: number | null;
  nombreProveedor?: string | null;
  nroCuentaComision?: number | null;
  total?: number | null;
  descripcion?: string | null;
}

export interface ComisionLiquidacionPagedResult {
  items: ComisionLiquidacionListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalImporte: number;
}

export interface ComisionLiquidacionDetalle {
  cabecera: ComisionLiquidacionListItem;
  documentosOrigen: DocumentoLiquidableDto[];
  concCom1?: string | null;
  concComImp1?: number | null;
  concCom2?: string | null;
  concComImp2?: number | null;
}

/** Base mostrada / usada según comisionBase del vendedor. */
export function importeBaseDocumento(doc: DocumentoLiquidableDto, comisionBase?: string | null): number {
  const base = (comisionBase ?? doc.comisionBase ?? '').trim().toLowerCase();
  if (base === 'importe total') {
    return Number(doc.impNetoMasImp) || 0;
  }
  return Number(doc.impNeto) || 0;
}

export function labelImporteBase(comisionBase?: string | null): string {
  const base = (comisionBase ?? '').trim().toLowerCase();
  return base === 'importe total' ? 'Importe total' : 'Importe neto';
}

/**
 * Mensaje de error para UI: mensaje (negocio) → message → primer errors → default.
 * getApiErrorMessage prioriza errors; acá priorizamos mensaje de LiquidacionComisionResultDto.
 */
export function getComisionErrorMessage(error: unknown, defaultMessage: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const mensaje = obj.mensaje ?? obj.Mensaje;
    if (typeof mensaje === 'string' && mensaje.trim()) return mensaje.trim();
    const message = obj.message ?? obj.Message;
    if (typeof message === 'string' && message.trim()) {
      // Si hay errors de validación, preferir el detalle del campo
      if (obj.errors && typeof obj.errors === 'object' && !Array.isArray(obj.errors)) {
        const errs = obj.errors as Record<string, unknown>;
        for (const key of Object.keys(errs)) {
          const val = errs[key];
          if (Array.isArray(val) && typeof val[0] === 'string' && val[0].trim()) {
            return val[0].trim();
          }
          if (typeof val === 'string' && val.trim()) return val.trim();
        }
      }
      return message.trim();
    }
  }
  if (typeof data === 'string' && data.trim()) return data.trim();
  return defaultMessage;
}

const todayIsoDate = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const comisionesService = {
  todayIsoDate,

  getLiquidaciones: async (
    params: ComisionLiquidacionListParams = {},
  ): Promise<ComisionLiquidacionPagedResult> => {
    const resp = await api.get<ComisionLiquidacionPagedResult>('/api/Comisiones', {
      params: {
        desde: params.desde || undefined,
        hasta: params.hasta || undefined,
        nroSuc: params.nroSuc || undefined,
        nroVendedor: params.nroVendedor || undefined,
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    });
    const data = resp.data;
    return {
      items: data?.items ?? [],
      totalCount: data?.totalCount ?? 0,
      pageNumber: data?.pageNumber ?? 1,
      pageSize: data?.pageSize ?? 20,
      totalPages: data?.totalPages ?? 0,
      totalImporte: data?.totalImporte ?? 0,
    };
  },

  getLiquidacion: async (nroFormulario: number): Promise<ComisionLiquidacionDetalle> => {
    const resp = await api.get<ComisionLiquidacionDetalle>(`/api/Comisiones/${nroFormulario}`);
    return resp.data;
  },

  getDocumentosLiquidables: async (
    body: DocumentosLiquidablesRequest,
  ): Promise<DocumentosLiquidablesResult> => {
    const payload: Record<string, unknown> = {
      nroVendedor: body.nroVendedor,
      desde: body.desde,
      hasta: body.hasta,
      soloTiposQueLiquidan: body.soloTiposQueLiquidan ?? true,
    };
    if (body.nroSuc != null && body.nroSuc > 0) {
      payload.nroSuc = body.nroSuc;
    }

    const resp = await api.post<DocumentosLiquidablesResult>(
      '/api/Comisiones/documentos-liquidables',
      payload,
    );
    const data = resp.data;
    return {
      nroVendedor: data?.nroVendedor ?? body.nroVendedor,
      nombreVendedor: data?.nombreVendedor ?? null,
      nroProveedor: data?.nroProveedor ?? null,
      nombreProveedor: data?.nombreProveedor ?? null,
      nroCuentaLiquidacion: data?.nroCuentaLiquidacion ?? null,
      nombreCuentaLiquidacion: data?.nombreCuentaLiquidacion ?? null,
      comisionPorc: data?.comisionPorc ?? null,
      comisionBase: data?.comisionBase ?? null,
      documentos: Array.isArray(data?.documentos) ? data.documentos : [],
      subtotalComision: Number(data?.subtotalComision) || 0,
      cantidadFilas: Number(data?.cantidadFilas) || 0,
    };
  },

  liquidar: async (body: LiquidarComisionRequest): Promise<LiquidarComisionResult> => {
    const resp = await api.post<LiquidarComisionResult>('/api/Comisiones/liquidar', {
      nroVendedor: body.nroVendedor,
      fechaEmision: body.fechaEmision,
      nroSuc: body.nroSuc,
      nroFormularios: body.nroFormularios,
      concCom1: body.concCom1 ?? '',
      concComImp1: body.concComImp1 ?? 0,
      concCom2: body.concCom2 ?? '',
      concComImp2: body.concComImp2 ?? 0,
    });
    const data = resp.data;
    return {
      exitoso: data?.exitoso === true,
      mensaje: data?.mensaje ?? null,
      nroFormularioComision: data?.nroFormularioComision ?? null,
      letra: data?.letra ?? null,
      nroSuc: data?.nroSuc ?? null,
      nroComprobante: data?.nroComprobante ?? null,
      formularioDisplay: data?.formularioDisplay ?? null,
      nroProveedor: data?.nroProveedor ?? null,
      nombreProveedor: data?.nombreProveedor ?? null,
      nroCuentaComision: data?.nroCuentaComision ?? null,
      totalComision: data?.totalComision ?? null,
      documentosLiquidados: data?.documentosLiquidados ?? null,
      nroFormulariosOrigen: data?.nroFormulariosOrigen ?? null,
    };
  },
};

export default comisionesService;
